import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GatewayDashboardService } from '../../../_services/gateway-dashboard.service';
import { AlertService } from '../../../_services';
import { fmtMoney, fmtCount, fmtRate, getTrendClass, isTrendNegative, getAbsTrend } from '../gateway-dashboard.helpers';
import {
  GatewaySummaryCard,
  CardBrandRow,
  GatewayTableRow,
  GatewayDashboardFilters,
  OverviewCard,
  DetailsRow,
  GatewayKpiSection,
  GatewayTypeSection,
  GatewayCardBrandSection,
  GatewayPaymentMethodSection,
  GatewayTableSection,
  GatewayKpiSectionData,
  GatewayTypeSectionData,
  GatewayCardBrandSectionData,
  GatewayPaymentMethodSectionData,
  GatewayTopProductsSectionData,
  GatewayTopCampaignsSectionData,
  CardBrandIcons,
  PaymentMethodColors,
  TypeMeta,
  GatewayReportField,
  GatewayKpiKey,
  GatewayTransactionType,
  TOP5_EXTRA,
} from './gateway-summary.data';

@Component({
  selector: 'app-gateway-summary',
  templateUrl: './gateway-summary.component.html',
  styleUrls: ['./gateway-summary.component.scss'],
})
export class GatewaySummaryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() filters: GatewayDashboardFilters = {};
  @Output() reportNavigate = new EventEmitter<string>();
  reportField = GatewayReportField;

  kpiSection: GatewayKpiSection = { ...GatewayKpiSectionData, row1: [...GatewayKpiSectionData.row1], row2: [...GatewayKpiSectionData.row2] };
  typeSection: GatewayTypeSection = { ...GatewayTypeSectionData, cards: [...GatewayTypeSectionData.cards] };
  cardBrandSection: GatewayCardBrandSection = { ...GatewayCardBrandSectionData };
  paymentMethodSection: GatewayPaymentMethodSection = { ...GatewayPaymentMethodSectionData, chartData: { ...GatewayPaymentMethodSectionData.chartData }, legend: [...GatewayPaymentMethodSectionData.legend] };
  topProductsSection: GatewayTableSection = { ...GatewayTopProductsSectionData };
  topCampaignsSection: GatewayTableSection = { ...GatewayTopCampaignsSectionData };

  protected _destroy$: Subject<boolean> = new Subject<boolean>();

  getTrendClass = getTrendClass;
  isTrendNegative = isTrendNegative;
  getAbsTrend = getAbsTrend;

  constructor(
    private gatewayDashboardService: GatewayDashboardService,
    private alertService: AlertService,
  ) {}

  ngOnInit() {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.filters && !changes.filters.firstChange) {
      this.loadData();
    }
  }

  navigateToReport(dimension: string) {
    this.reportNavigate.emit(dimension);
  }

  ngOnDestroy() {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  loadData() {
    // Reset all sections to initial loading state
    this.kpiSection = { ...GatewayKpiSectionData, row1: [...GatewayKpiSectionData.row1], row2: [...GatewayKpiSectionData.row2] };
    this.typeSection = { ...GatewayTypeSectionData, cards: [...GatewayTypeSectionData.cards] };
    this.cardBrandSection = { ...GatewayCardBrandSectionData };
    this.paymentMethodSection = { ...GatewayPaymentMethodSectionData, chartData: { ...GatewayPaymentMethodSectionData.chartData }, legend: [...GatewayPaymentMethodSectionData.legend] };
    this.topProductsSection = { ...GatewayTopProductsSectionData };
    this.topCampaignsSection = { ...GatewayTopCampaignsSectionData };

    // ── KPI cards (overview_cards) ─────────────────────────────────────────
    this.gatewayDashboardService.getKpiCards(this.filters)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        response => {
          this.kpiSection.loading = false;
          if (response && response.cards && response.cards.length) {
            this.mapKpiCards(response.cards);
          } else {
            this.kpiSection.noData = true;
          }
        },
        error => {
          this.kpiSection.loading = false;
          this.kpiSection.noData = true;
          this.alertService.error(error.message);
        }
      );

    // ── Transaction-type cards (details_table / sales_type) ────────────────
    this.gatewayDashboardService.getDetailsTable(this.filters, [GatewayReportField.SalesType])
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        response => {
          this.typeSection.loading = false;
          if (response && response.results) {
            this.mapTypeCards(response.results);
          } else {
            this.typeSection.noData = true;
          }
        },
        error => {
          this.typeSection.loading = false;
          this.typeSection.noData = true;
          this.alertService.error(error.message);
        }
      );

    // ── Card Brand (details_table / card_type) ─────────────────────────────
    this.gatewayDashboardService.getDetailsTable(this.filters, [GatewayReportField.CardType], TOP5_EXTRA)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        response => {
          this.cardBrandSection.loading = false;
          if (response && response.results && response.results.length) {
            this.mapCardBrand(response.results, response.summary_row);
          } else {
            this.cardBrandSection.noData = true;
          }
        },
        error => {
          this.cardBrandSection.loading = false;
          this.cardBrandSection.noData = true;
          this.alertService.error(error.message);
        }
      );

    // ── Payment Method (details_table / payment_method) ────────────────────
    this.gatewayDashboardService.getDetailsTable(this.filters, [GatewayReportField.PaymentMethod], TOP5_EXTRA)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        response => {
          this.paymentMethodSection.loading = false;
          if (response && response.results && response.results.length) {
            this.mapPaymentMethod(response.results);
          } else {
            this.paymentMethodSection.noData = true;
            this.paymentMethodSection.chartData.noData = true;
          }
        },
        error => {
          this.paymentMethodSection.loading = false;
          this.paymentMethodSection.noData = true;
          this.paymentMethodSection.chartData.noData = true;
          this.alertService.error(error.message);
        }
      );

    // ── Top Products (details_table / product_name) ────────────────────────
    this.gatewayDashboardService.getDetailsTable(this.filters, [GatewayReportField.ProductName], TOP5_EXTRA)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        response => {
          this.topProductsSection.loading = false;
          if (response && response.results && response.results.length) {
            this.topProductsSection.rows = this.mapTableRows(response.results);
          } else {
            this.topProductsSection.noData = true;
          }
        },
        error => {
          this.topProductsSection.loading = false;
          this.topProductsSection.noData = true;
          this.alertService.error(error.message);
        }
      );

    // ── Top Campaigns (details_table / campaign_name) ──────────────────────
    this.gatewayDashboardService.getDetailsTable(this.filters, [GatewayReportField.CampaignName], TOP5_EXTRA)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        response => {
          this.topCampaignsSection.loading = false;
          if (response && response.results && response.results.length) {
            this.topCampaignsSection.rows = this.mapTableRows(response.results);
          } else {
            this.topCampaignsSection.noData = true;
          }
        },
        error => {
          this.topCampaignsSection.loading = false;
          this.topCampaignsSection.noData = true;
          this.alertService.error(error.message);
        }
      );
  }

  // ── Mapping ─────────────────────────────────────────────────────────────────

  private mapKpiCards(cards: OverviewCard[]): void {
    const cardMap: { [key: string]: OverviewCard } = {};
    cards.forEach(card => {
      cardMap[card.key] = card;
    });

    const get = (key: string): { value: number; trend_pct: number | null } => {
      let result: { value: number; trend_pct: number | null } = { value: 0, trend_pct: null };
      if (cardMap[key]) {
        result = {
          value: cardMap[key].value || 0,
          trend_pct: cardMap[key].trend_pct !== undefined ? cardMap[key].trend_pct : null,
        };
      }
      return result;
    };

    const grossRevenue = get(GatewayKpiKey.GrossRevenue);
    const transactions = get(GatewayKpiKey.Transactions);
    const approvals = get(GatewayKpiKey.Approvals);
    const declines = get(GatewayKpiKey.Declines);
    const pending = get(GatewayKpiKey.Pending);
    const netRevenue = get(GatewayKpiKey.NetRevenue);
    const refundAmount = get(GatewayKpiKey.RefundAmount);
    const refundCount = get(GatewayKpiKey.RefundCount);
    const averageOrderValue = get(GatewayKpiKey.AverageOrderValue);
    const approvalRate = get(GatewayKpiKey.ApprovalRate);
    const pendingSettlement = get(GatewayKpiKey.PendingSettlement);
    const pendingSettlementCount = get(GatewayKpiKey.PendingSettlementCount);

    this.kpiSection.row1 = [
      {
        title: 'Gross Revenue',
        value: fmtMoney(grossRevenue.value),
        trend_pct: grossRevenue.trend_pct,
        breakdown: [{
          label: 'Pending Settlement',
          value: fmtMoney(pendingSettlement.value),
          hint: 'Transactions awaiting settlement from the payment processor.',
          count: fmtCount(pendingSettlementCount.value),
        }],
      },
      {
        title: 'Transactions',
        value: fmtCount(transactions.value),
        trend_pct: transactions.trend_pct,
        breakdown: [
          { label: 'Approved', value: fmtCount(approvals.value) },
          { label: 'Declined', value: fmtCount(declines.value) },
          { label: 'Pending',  value: fmtCount(pending.value) },
        ],
      },
      {
        title: 'Net Revenue',
        hint: 'Gross revenue minus refunds and chargebacks.',
        value: fmtMoney(netRevenue.value),
        trend_pct: netRevenue.trend_pct,
      },
      {
        title: 'Refunds',
        value: fmtMoney(refundAmount.value),
        trend_pct: refundAmount.trend_pct,
        breakdown: [{ label: 'Quantity', value: fmtCount(refundCount.value) }],
      },
    ];

    this.kpiSection.row2 = [
      {
        title: 'Average Order Value',
        hint: 'Total revenue divided by number of approved transactions.',
        value: fmtMoney(averageOrderValue.value),
        trend_pct: averageOrderValue.trend_pct,
      },
      {
        title: 'Approval Rate',
        hint: 'Percentage of transactions that were approved out of all attempted.',
        value: fmtRate(approvalRate.value),
        trend_pct: approvalRate.trend_pct,
      },
    ];

    this.kpiSection.noData = grossRevenue.value === 0 && transactions.value === 0;
  }

  private mapTypeCards(rows: DetailsRow[]): void {
    this.typeSection.cards = [GatewayTransactionType.Initial, GatewayTransactionType.Rebill, GatewayTransactionType.OneTime].map(dim => {
      const meta = TypeMeta[dim];
      const r = rows.find(row => String(row.dimension) === String(dim));
      if (r) {
        return {
          title: meta.title, icon: meta.icon, iconBg: meta.iconBg,
          value: fmtCount(r.transactions),
          trend_pct: r.trend_pct !== undefined ? r.trend_pct : null,
          breakdown: [
            { label: 'Gross Revenue', value: fmtMoney(r.gross_revenue || 0) },
            { label: 'Approved', value: fmtCount(r.approvals || 0) },
            { label: 'Approval Rate', value: fmtRate(r.approval_rate || 0), valueColor: '#0CBE68' },
          ],
        };
      }
      return {
        title: meta.title, icon: meta.icon, iconBg: meta.iconBg,
        value: '--', trend_pct: null,
        breakdown: [
          { label: 'Gross Revenue', value: '$0.00' },
          { label: 'Approved', value: '--' },
          { label: 'Approval Rate', value: '--', valueColor: '#0CBE68' },
        ],
      };
    });
    this.typeSection.noData = rows.length === 0;
  }

  private mapCardBrand(rows: DetailsRow[], summaryRow: DetailsRow | null): void {
    const toRow = (r: DetailsRow, isTotal = false): CardBrandRow => ({
      name: isTotal ? 'Total' : String(r.dimension),
      icon: CardBrandIcons[String(r.dimension)],
      isTotal,
      transactions: fmtCount(r.transactions),
      grossRevenue: fmtMoney(r.gross_revenue || 0),
      approvalRate: fmtRate(r.approval_rate || 0),
      refundRate: fmtRate(r.refund_rate || 0),
      refundAmount: fmtMoney(r.refund_amount || 0),
      netRevenue: fmtMoney(r.net_revenue || 0),
    });

    this.cardBrandSection.rows = rows.map(r => toRow(r));
    this.cardBrandSection.total = summaryRow ? toRow(summaryRow, true) : null;
    this.cardBrandSection.noData = rows.length === 0;
  }

  private mapPaymentMethod(rows: DetailsRow[]): void {
    const totalRevenue = rows.reduce((sum, r) => sum + (r.net_revenue || 0), 0);
    this.paymentMethodSection.chartData = {
      data: rows.map(r => r.transactions || 0),
      labels: rows.map(r => String(r.dimension)),
      backgroundColors: [{ backgroundColor: rows.map((_, i) => PaymentMethodColors[i % PaymentMethodColors.length]) }],
      total: fmtMoney(totalRevenue),
      noData: false,
      loading: false,
    };
    this.paymentMethodSection.legend = rows.map((r, i) => ({
      name: String(r.dimension),
      color: PaymentMethodColors[i % PaymentMethodColors.length],
      transactions: fmtCount(r.transactions),
      revenue: fmtMoney(r.net_revenue || 0),
      approvalRate: fmtRate(r.approval_rate || 0),
    }));
    this.paymentMethodSection.noData = rows.length === 0;
  }

  private mapTableRows(rows: DetailsRow[]): GatewayTableRow[] {
    return rows.map(r => ({
      name: String(r.dimension),
      netRevenue: fmtMoney(r.net_revenue || 0),
      transactions: fmtCount(r.transactions),
      approvalRate: fmtRate(r.approval_rate || 0),
    }));
  }

  hasBreakdown(card: GatewaySummaryCard): boolean {
    let result = false;
    if (card.breakdown && card.breakdown.length > 0) {
      result = true;
    }
    return result;
  }
}
